<template>
  <div class="project-internal">
    <div class="accordion" role="tablist">
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-internal-basic-data
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-info-circle mr-2"></i>{{ $t('basicData') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-internal-basic-data" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <b-row>
              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  v-if="isAdmin"
                  horizontal
                  :label="$t('client')"
                  label-for="client"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('client')"
                    v-model="project.client"
                    id="employer"
                    :options="clients"
                    :allow-empty="false"
                    :show-labels="false"
                    :disabled="isClient"
                    deselectLabel
                    track-by="number"
                    label="name"
                  >
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('employer')"
                  label-for="employer"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('employer')"
                    v-model="project.employer"
                    id="employer"
                    :options="employers"
                    :allow-empty="true"
                    :show-labels="false"
                    deselectLabel
                    track-by="number"
                    label="name"
                  >
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('inCharge')"
                  label-for="inCharge"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    class="multiselect-users"
                    :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
                    v-model="project.inCharge"
                    :options="filteredUsers"
                    track-by="number"
                    :internal-search="false"
                    :show-labels="false"
                    :allow-empty="true"
                    :deselectLabel="$t('deselectLabel')"
                    selectLabel=""
                    selectedLabel=""
                    v-on:search-change="onUserSearch"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      {{ option.firstname }} {{ option.lastname }}
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      {{ option.firstname }} {{ option.lastname }}
                    </template>
                    <template slot="noResult">
                      <div>{{ $t('no_results') }}</div>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('inChargeSales')"
                  label-for="inChargeSales"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    class="multiselect-users"
                    :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
                    v-model="salesInCharge"
                    :options="filteredUsers"
                    track-by="number"
                    :internal-search="false"
                    :show-labels="false"
                    :allow-empty="true"
                    :disabled="true"
                    :deselectLabel="$t('deselectLabel')"
                    selectLabel=""
                    selectedLabel=""
                    v-on:search-change="onUserSearch"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      {{ option.firstname }} {{ option.lastname }}
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      {{ option.firstname }} {{ option.lastname }}
                    </template>
                    <template slot="noResult">
                      <div>{{ $t('no_results') }}</div>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('customerNumber')"
                  label-for="customerNumber"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <b-form-input
                    v-model="project.customer.number"
                    type="text"
                    :placeholder="$t('customerNumber')"
                  />
                </b-form-group>
              </b-col>

              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  horizontal
                  :label="$t('employerProjectNumber')"
                  label-for="employerProjectNumber"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <b-form-input
                    v-model="project.employerProjectNumber"
                    type="text"
                    :placeholder="$t('employerProjectNumber')"
                  />
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('deliveryAt')"
                  label-for="deliveryAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="deliveryAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  >
                  </Calendar>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('businessModelState')"
                  label-for="businessModelState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('businessModelState')"
                    v-model="project.businessModelState"
                    id="businessModelState"
                    :options="getEnumValues('BusinessModel')"
                    :allow-empty="true"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('projectTypeState')"
                  label-for="projectTypeState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('projectTypeState')"
                    v-model="project.projectTypeState"
                    id="projectTypeState"
                    :options="getEnumValues('ProjectTypeState')"
                    :allow-empty="true"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge :variant="projectTypeStateColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge :variant="projectTypeStateColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('projectState')"
                  label-for="projectState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('projectState')"
                    v-model="project.projectState"
                    id="projectState"
                    :options="getEnumValues('ProjectState')"
                    :allow-empty="false"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge :variant="projectStateColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge :variant="projectStateColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-collapse>
      </div>
    </div>

    <div class="accordion" role="tablist">
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-internal-short-teaser
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-file-pdf-o mr-2"></i>{{ $t('shortTeaser') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-internal-short-teaser" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <ProjectShortTeaser
              :project="project"
              v-on:project-short-teaser-attachment-list:delete="
                $emit('project-short-teaser-attachment-list:delete', $event)
              "
            />
          </div>
        </b-collapse>
      </div>
    </div>

    <div class="accordion" role="tablist">
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-internal-common
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-cogs mr-2"></i>{{ $t('common') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-internal-common" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <b-row>
              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  horizontal
                  :label="$t('billingModelState')"
                  label-for="billingModelState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('billingModelState')"
                    v-model="project.billingModelState"
                    id="billingModelState"
                    :options="getEnumValues('BillingModel')"
                    :allow-empty="true"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('businessUnitState')"
                  label-for="businessUnitState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('businessUnitState')"
                    v-model="project.businessUnitState"
                    id="businessUnitState"
                    :options="getEnumValues('BusinessUnit')"
                    :allow-empty="true"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge variant="secondary">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('contractSignedState')"
                  label-for="contractSignedState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('contractSignedState')"
                    v-model="project.contractSignedState"
                    id="contractSignedState"
                    :options="yesNo()"
                    :allow-empty="false"
                    :show-labels="false"
                    deselectLabel
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge :variant="yesNoColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge :variant="yesNoColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('planningHorizonYear')"
                  label-for="planningHorizonYear"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="planningHorizonYearCalendar"
                    view="year"
                    dateFormat="yy"
                    :placeholder="$t('planningHorizonYear')"
                    :yearNavigator="true"
                    :manualInput="true"
                  />
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('probabilityOfImplementation')"
                  label-for="probabilityOfImplementation"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <InputNumber
                    v-model="project.probabilityOfImplementation"
                    :min="0"
                    :max="100"
                    suffix=" %"
                    :placeholder="$t('probabilityOfImplementation')"
                  />
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('vatPercentageState')"
                  label-for="vatPercentageState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Dropdown
                    v-model="project.vatPercentageState"
                    :options="vatPercentageOptions"
                    :placeholder="$t('vatPercentageState')"
                    optionLabel="label"
                    optionValue="value"
                    :showClear="true"
                  />
                </b-form-group>
              </b-col>
              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  horizontal
                  :label="$t('generalBusinessCosts')"
                  label-for="generalBusinessCosts"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <InputNumber
                    v-model="displayedGeneralBusinessCosts"
                    mode="decimal"
                    :minFractionDigits="2"
                    :maxFractionDigits="2"
                    suffix=" %"
                    locale="de-DE"
                    :placeholder="generalBusinessCostsPlaceholder"
                    :disabled="!overrideGeneralBusinessCosts"
                  />
                </b-form-group>
                <b-form-group
                  horizontal
                  label-for="generalBusinessCostsOverridden"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <b-form-checkbox
                    :checked="overrideGeneralBusinessCosts"
                    id="generalBusinessCostsOverridden"
                    @change="onGeneralBusinessCostsOverrideChange"
                  >
                    {{ $t('generalBusinessCostsOverridden') }}
                  </b-form-checkbox>
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('profitMargin')"
                  label-for="profitMargin"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <InputNumber
                    v-model="displayedProfitMargin"
                    mode="decimal"
                    :minFractionDigits="2"
                    :maxFractionDigits="2"
                    suffix=" %"
                    locale="de-DE"
                    :disabled="!overrideProfitMargin"
                  />
                </b-form-group>
                <b-form-group
                  horizontal
                  label-for="profitMarginOverridden"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <b-form-checkbox
                    :checked="overrideProfitMargin"
                    id="profitMarginOverridden"
                    @change="onProfitMarginOverrideChange"
                  >
                    {{ $t('profitMarginOverridden') }}
                  </b-form-checkbox>
                </b-form-group>

                <!-- <b-form-group
                  horizontal
                  :label="$t('technichalReviewAt')"
                  label-for="technichalReviewAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="technichalReviewAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group> -->

                <b-form-group
                  horizontal
                  :label="$t('offerSentAt')"
                  label-for="offerSentAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="offerSentAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group>

                <b-form-group
                  horizontal
                  :label="$t('orderConfirmationSentAt')"
                  label-for="orderConfirmationSentAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="orderConfirmationSentAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group>

                <!-- <b-form-group
                  horizontal
                  :label="$t('consumablesOrderedAt')"
                  label-for="consumablesOrderedAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="consumablesOrderedAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group> -->

                <!-- <b-form-group
                  horizontal
                  :label="$t('consumablesDeliveredAt')"
                  label-for="consumablesDeliveredAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="consumablesDeliveredAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group> -->

                <!-- <b-form-group
                  horizontal
                  :label="$t('acceptancePlannedAt')"
                  label-for="acceptancePlannedAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="acceptancePlannedAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group> -->

                <!-- <b-form-group
                  horizontal
                  :label="$t('acceptanceCompletedAt')"
                  label-for="acceptanceCompletedAt"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Calendar
                    v-model="acceptanceCompletedAt"
                    selectionMode="single"
                    dateFormat="dd.mm.yy"
                    :placeholder="$t('date')"
                    :showWeek="true"
                    :yearNavigator="true"
                    :monthNavigator="true"
                  />
                </b-form-group> -->
              </b-col>
            </b-row>
          </div>
        </b-collapse>
      </div>
    </div>

    <div class="accordion" role="tablist">
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-internal-calculation-advanced
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-calculator mr-2"></i>{{ $t('calculation') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-internal-calculation-advanced" role="tabpanel" visible>
          <div class="accordion-content px-2 py-2">
            <ProjectCalculationAdvanced :project="project" />
          </div>
        </b-collapse>
      </div>
    </div>

    <div class="accordion" role="tablist" v-if="project.deferredState">
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-internal-deferred-state
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-clock-o mr-2"></i>{{ $t('deferredState') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-internal-deferred-state" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <b-row>
              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  horizontal
                  :label="$t('deferredState')"
                  label-for="deferredState"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <multiselect
                    :placeholder="$t('deferredState')"
                    v-model="project.deferredState.deferred"
                    :options="yesNo()"
                    :allow-empty="false"
                    :show-labels="false"
                    deselectLabel
                    @select="onChangeProjectDeferredState"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <b-badge :variant="yesNoStateReversedColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <b-badge :variant="yesNoStateReversedColor(option)">
                        {{ $t(option) }}
                      </b-badge>
                    </template>
                  </multiselect>
                </b-form-group>

                <b-form-group
                  v-if="
                    project.deferredState.deferred === 'YES' ||
                      project.deferredState.description != '' ||
                      project.deferredState.comment != ''
                  "
                  horizontal
                  :label="$t('reason')"
                  label-for="reason"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <div class="form-control-plaintext">
                    {{ $t(project.deferredState.reason) }}
                  </div>
                </b-form-group>
              </b-col>

              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  v-if="
                    project.deferredState.deferred === 'YES' ||
                      project.deferredState.description != '' ||
                      project.deferredState.comment != ''
                  "
                  horizontal
                  :label="$t('description')"
                  label-for="deferredDescription"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Textarea
                    id="deferredDescription"
                    v-model="project.deferredState.description"
                    rows="3"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    project.deferredState.deferred === 'YES' ||
                      project.deferredState.description != '' ||
                      project.deferredState.comment != ''
                  "
                  horizontal
                  :label="$t('comment')"
                  label-for="deferredComment"
                  label-class="text-md-right"
                  :label-cols="4"
                  breakpoint="md"
                >
                  <Textarea id="deferredComment" v-model="project.deferredState.comment" rows="3" />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-collapse>
      </div>
    </div>

    <Dialog
      :header="$t('reasonRequired')"
      :visible.sync="displayStateChangeConfirmation"
      :contentStyle="{ overflow: 'visible' }"
      :modal="true"
    >
      <div v-if="project.deferredState">
        <div class="flex-row">
          <div>{{ $t('reason') }}</div>
          <div>
            <Dropdown
              v-model="project.deferredState.reason"
              :options="getEnumValues('DeferredReason')"
              :placeholder="$t('select')"
            >
              <template #option="slotProps">
                <b-badge>
                  {{ $t(slotProps.option) }}
                </b-badge>
              </template>
              <template #value="slotProps">
                <b-badge>
                  {{ $t(slotProps.value) }}
                </b-badge>
              </template>
            </Dropdown>
          </div>
        </div>

        <div class="flex-row">
          <div>{{ $t('description') }}</div>
          <div>
            <Textarea v-model="project.deferredState.description" rows="3" cols="40" />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          class="btn btn-narrow btn-default ml-2 mb-2"
          @click="onCancelProjectDeferredStateChangeReason"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          class="btn btn-narrow btn-inverse ml-2 mb-2"
          @click="onSaveProjectDeferredStateChangeReason"
          :disabled="deferredStateOkButtonDisabled"
        >
          {{ $t('ok') }}
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import InputNumber from 'primevue/inputnumber';
import {
  getYesNoStateColor,
  getDocumentStateColor,
  getProjectStateColor,
  yesNoStateReversedColor,
  projectTypeStateColor,
} from '@/helpers/colors';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import { yesNo } from '@/helpers/enums';
import Dropdown from 'primevue/dropdown';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import ProjectShortTeaser from '@/views/Projects/ProjectShortTeaser';
import ProjectCalculationAdvanced from '@/views/Projects/ProjectCalculationAdvanced';

export default {
  components: {
    Multiselect,
    Calendar,
    InputNumber,
    Dialog,
    Textarea,
    Dropdown,
    ProjectShortTeaser,
    ProjectCalculationAdvanced,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      overrideProfitMargin: { type: Boolean, default: false },
      displayStateChangeConfirmation: false,
      projectStateBackup: null,
      vatPercentageOptions: [
        { value: 19, label: '19 %' },
        { value: 0, label: '0 %' },
      ],
      overrideGeneralBusinessCosts: false,
      editor: InlineEditor,
      editorConfig: {},
      filteredUsers: [],
      usersFilter: {
        pagination: {
          skip: 0,
          pageSize: 200,
        },
        sorting: {
          sortField: 'lastname',
          sortOrder: 1,
        },
        filterName: 'project-internal-user-filters',
        filters: {
          userSearch: { value: null },
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters(['getEnumValues', 'getClients', 'getEmployers', 'getUsers', 'getItemSettings']),

    itemSettings() {
      return this.getItemSettings || {};
    },

    displayedProfitMargin: {
      get() {
        if (this.overrideProfitMargin) {
          return this.project.profitMargin;
        }
        return this.itemSettings?.profitMargin ?? null;
      },
      set(value) {
        if (this.overrideProfitMargin) {
          this.project.profitMargin = value;
        }
      },
    },
    profitMarginPlaceholder() {
      if (this.overrideProfitMargin) {
        return '';
      }
      const globalMargin = this.itemSettings?.profitMargin;
      if (globalMargin != null) {
        return `${this.$t('profitMargin')}: ${globalMargin} % (aus Einstellungen)`;
      }
      return this.$t('profitMargin');
    },
    displayedGeneralBusinessCosts: {
      get() {
        if (this.overrideGeneralBusinessCosts) {
          return this.project.generalBusinessCosts;
        }
        return this.itemSettings?.generalBusinessCosts ?? null;
      },
      set(value) {
        if (this.overrideGeneralBusinessCosts) {
          this.project.generalBusinessCosts = value;
        }
      },
    },
    generalBusinessCostsPlaceholder() {
      if (this.overrideGeneralBusinessCosts) {
        return '';
      }
      const globalValue = this.itemSettings?.generalBusinessCosts;
      if (globalValue != null) {
        return `${this.$t('generalBusinessCosts')}: ${globalValue} % (aus Einstellungen)`;
      }
      return this.$t('generalBusinessCosts');
    },
    employers() {
      // return this.project.client && this.project.client.employers
      //   ? this.project.client.employers
      //   : [];
      return this.getEmployers.filter((employer) => {
        if (
          employer.client &&
          this.project.client &&
          employer.client.id === this.project.client.id
        ) {
          return employer;
        }
      });
    },
    clients() {
      return this.isClient ? [this.getCurrentUser.client] : this.getClients;
    },
    deliveryAt: {
      get() {
        return this.$calendarFormat(this.project.deliveryAt);
      },
      set(value) {
        this.project.deliveryAt = value;
      },
    },
    technichalReviewAt: {
      get() {
        return this.$calendarFormat(this.project.technichalReviewAt);
      },
      set(value) {
        this.project.technichalReviewAt = value;
      },
    },
    offerSentAt: {
      get() {
        return this.$calendarFormat(this.project.offerSentAt);
      },
      set(value) {
        this.project.offerSentAt = value;
      },
    },
    orderConfirmationSentAt: {
      get() {
        return this.$calendarFormat(this.project.orderConfirmationSentAt);
      },
      set(value) {
        this.project.orderConfirmationSentAt = value;
      },
    },
    consumablesOrderedAt: {
      get() {
        return this.$calendarFormat(this.project.consumablesOrderedAt);
      },
      set(value) {
        this.project.consumablesOrderedAt = value;
      },
    },
    consumablesDeliveredAt: {
      get() {
        return this.$calendarFormat(this.project.consumablesDeliveredAt);
      },
      set(value) {
        this.project.consumablesDeliveredAt = value;
      },
    },
    acceptancePlannedAt: {
      get() {
        return this.$calendarFormat(this.project.acceptancePlannedAt);
      },
      set(value) {
        this.project.acceptancePlannedAt = value;
      },
    },
    acceptanceCompletedAt: {
      get() {
        return this.$calendarFormat(this.project.acceptanceCompletedAt);
      },
      set(value) {
        this.project.acceptanceCompletedAt = value;
      },
    },
    planningHorizonYearCalendar: {
      get() {
        const y = this.project.planningHorizonYear;
        return y != null && Number.isFinite(y) ? new Date(y, 0, 1) : null;
      },
      set(value) {
        if (value == null) {
          this.project.planningHorizonYear = null;
          return;
        }
        if (value instanceof Date && !isNaN(value.getTime())) {
          this.project.planningHorizonYear = value.getFullYear();
          return;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
          this.project.planningHorizonYear = value;
          return;
        }
        const parsed = typeof value === 'string' ? parseInt(value, 10) : NaN;
        this.project.planningHorizonYear = Number.isFinite(parsed) ? parsed : null;
      },
    },
    salesInCharge: {
      get() {
        return this.project.sales?.inCharge || null;
      },
      set(value) {
        if (!this.project.sales) {
          this.project.sales = {};
        }
        this.project.sales.inCharge = value;
      },
    },
    /**
     * @todo DRY
     */
    invalidFeedbackNA() {
      return this.$t('preconditionNA');
    },
    deferredStateOkButtonDisabled() {
      return this.project.deferredState && this.project.deferredState.description !== ''
        ? false
        : true;
    },
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'fetchClients',
      'fetchEmployersByClient',
      'fetchEmployers',
      'fetchUsersPaginated',
      'fetchSettingsByCategory',
    ]),
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    documentStateColor(state) {
      return getDocumentStateColor(state);
    },
    projectStateColor(state) {
      return getProjectStateColor(state);
    },

    onProfitMarginOverrideChange(eventOrChecked) {
      if (eventOrChecked) {
        this.overrideProfitMargin = true;
      } else {
        this.overrideProfitMargin = false;
        this.project.profitMargin = null;
      }
    },

    onGeneralBusinessCostsOverrideChange(eventOrChecked) {
      if (eventOrChecked) {
        this.overrideGeneralBusinessCosts = true;
      } else {
        this.overrideGeneralBusinessCosts = false;
        this.project.generalBusinessCosts = null;
      }
    },

    /**
     * if project.deferredState is changed to yes a reason has to be given
     * @param {*} newState
     */
    onChangeProjectDeferredState(newState) {
      if (newState === 'YES') {
        this.displayStateChangeConfirmation = true;
      }
    },

    onCancelProjectDeferredStateChangeReason() {
      this.project.deferredState.deferred = 'NO';
      this.displayStateChangeConfirmation = false;
    },
    onSaveProjectDeferredStateChangeReason() {
      this.displayStateChangeConfirmation = false;
    },

    async setup() {
      if (this.isClient) {
        await this.fetchEmployersByClient(this.getCurrentUser.client.id);
      } else {
        await this.fetchClients();
        await this.fetchEmployers();
      }
      await this.fetchEnumValues('ProjectState');
      await this.fetchEnumValues('ProjectTypeState');
      await this.fetchEnumValues('BusinessModel');
      await this.fetchEnumValues('BillingModel');
      await this.fetchEnumValues('BusinessUnit');
      await this.fetchEnumValues('AskEmployerCategoryState');
      await this.fetchEnumValues('DocumentState');
      await this.fetchEnumValues('DeferredReason');
      await this.loadUserData();

      /** init deferredState */
      if (!this.project.deferredState) {
        this.project.deferredState = {
          deferred: 'NO',
          reason: 'MISC',
          description: '',
          comment: '',
        };
      }

      // cache current projectState
      this.projectStateBackup = this.project.projectState;
    },
    /**
     * check requirementsValidState to show mark input fields
     * @todo DRY
     */
    requirementsValidState(field) {
      if (!this.project.netRegistrationRequirements) return false;

      const requirement = this.project.netRegistrationRequirements.find(
        (requirement) => requirement.name === field
      );
      return requirement && requirement.fullfilledState === 'YES' ? true : false;
    },
    /**
     * Fetch user data from graphql
     */
    async loadUserData() {
      await this.fetchUsersPaginated({
        pagination: this.usersFilter.pagination,
        sorting: this.usersFilter.sorting,
        filters: { ...this.usersFilter.filters },
      });
      this.filteredUsers = this.getUsers;
    },
    /**
     * search in dropdown for a user to assign to the project
     */
    async onUserSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.usersFilter.filters.userSearch.value = query;
      await this.loadUserData();
    },
    yesNo,
    yesNoStateReversedColor,
    projectTypeStateColor,
  },

  async mounted() {
    await this.fetchSettingsByCategory('ITEM');
    this.overrideProfitMargin = this.project.profitMargin != null;
    this.overrideGeneralBusinessCosts = this.project.generalBusinessCosts != null;
    await this.setup();
  },
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}

.form-grid-container-left .label {
  text-align: right;
}

.dense-editor {
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.ck-editor__editable_inline {
  min-height: 110px;
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.multiselect-users {
  min-width: 230px;
  display: inline-block;
}

::v-deep .multiselect__tags {
  border: 0;
  border-radius: 0;
}

::v-deep .multiselect-users .multiselect__input {
  padding-top: 1em;
}
</style>
